import React, { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ProductImage } from 'audamatic-ui';
import Badge from '../../base_badges/component';
import { getDominantBackgroundColor, getHueClass } from '@utils';
import Countdown from '../../ui_countdown/component';
import Link from '../../base_link/component';
import Icon from '../../base_svg-icon/component';
import { t } from '@utils/i18n';
import { Product } from '@utils/product';

import Shape from '@active-channel/images/shapes/medium-banner-shape.svg';

import './styles/styles.scss';

class BannerSingleProduct extends Component {
  constructor(props) {
    super(props);
    this.state = {
      backgroundClass: 'hue-default',
    };
    this.imageRef = createRef();
  }

  get promotionTagData() {
    const { text1, text2, variant } = this.props;
    return {
      title: text2,
      subtitle: text1,
      'component-type': this.props['component-type'],
      variant,
      url: this.url,
    };
  }


  pushGoogleTags = () => {
    this.props.googleTagMethods()
      .pushPromotionGoogleTag(this.promotionTagData);
  };

  /**
   * get all products from props
   * The provided products can be a combination of manually added products
   * and dynamically addded products.
   * @returns {array}
   */
  get combinedProducts() {
    const manualyAdded = this.props.products || [];
    const dynamicalyAdded = this.props.productSelection || [];
    const combinedProducts = [
      ...manualyAdded,
      ...dynamicalyAdded,
    ];
    return combinedProducts.slice(0, 4);
  }

  get store() {
    return this.props.store || {};
  }

  get amount() {
    switch (this.combinedProducts.length) {
    case 1:
      return 'one-product';
    case 2:
      return 'two-products';
    case 3:
      return 'three-products';
    case 4:
      return 'four-products';
    default: return '';
    }
  }

  get url() {
    return this.props.link || (!!this.combinedProducts.length && this.combinedProducts[0].url) || '';
  }

  get booklist() {
    const { variant } = this.props;
    const className =  variant === 'timedDeal' ? 'hascountdown' : '';
    return this.combinedProducts.map((_product, index) => {
      const product = new Product(_product);
      return (
        <Fragment key={index}>
          <div className={`product-image-wrapper ${className}`}>
            <ProductImage
              product={product}
            />
          </div>
          {((this.combinedProducts.length > 1 && index === 1) || (this.combinedProducts.length === 1 && index === 0)) && (
            <img
              alt={product.name}
              onLoad={this.setBackgroundColor}
              style={{ display: 'none' }}
              ref={this.imageRef}
              src={product.image}
            />
          )}
        </Fragment>
      );
    });
  }

  setBackgroundColor = () => {
    this.setState({ backgroundClass: this.backgroundColorClass });
  };

  get backgroundColorClass() {
    if (!!this.imageRef.current) {
      return getDominantBackgroundColor(this.imageRef.current);
    }
    return 'hue-default';
  }

  renderImages() {
    const { backgroundImage, behindCoverImage, inFrontOfCoverImage, text2 } = this.props;
    return (
      <>
        {!!backgroundImage && (
          <div className="bg-img-container">
            <img
              src={backgroundImage}
              alt={text2}
              className="bg-img"
            />
          </div>
        )}
        {!!behindCoverImage && (
          <img className="underlayer" src={behindCoverImage} alt={text2}/>
        )}
        {!!inFrontOfCoverImage && (
          <img className="overlayer" src={inFrontOfCoverImage} alt={text2}/>
        )}
      </>
    );
  }

  renderPrices() {
    if (!this.combinedProducts.length) {
      return;
    }
    const product = new Product(this.combinedProducts[0]);
    const { price1, price2 } = product.getProductPrice();
    if (!price1 && !price2) {
      return;
    }

    return (
      <div className="pricing">
        {price1 && (
          <div className="default-price">
            {`${price2 ? t('discount.from-price') : t('discount.to-price')} ${price1}`}
          </div>
        )}
        {price2 && (
          <div className="discount-price">
            {`${t('discount.to-price')} ${price2}`}
          </div>
        )}
      </div>
    );
  }

  get additionalParameters() {
    if (!!this.props.additionalParameters) {
      return JSON.parse(this.props.additionalParameters);
    }
    return {};
  }

  render() {
    const { variant, text1, text2, text3, backgroundImage, colourCode, badgeType, badgeImage, badgeText, badgePromoType, badgePriceInput } = this.props;
    const backgroundClass = (colourCode) ? getHueClass(colourCode) : this.state.backgroundClass;

    return (
      <div className="banner-single-product">
        <Link
          to={this.url}
          onClick={this.pushGoogleTags}
          className={`card ${backgroundClass}`}
        >
          <div className="card-wrapper">
            {!backgroundImage && (
              <div className="artwork">
                <Shape height="100%"/>
              </div>
            )}
            <div className={`card-media ${this.amount}`}>
              <div className="artwork-cutoff">
                {this.renderImages()}
              </div>
              <div className="product-wrap">
                {this.booklist}
              </div>
              {variant === 'timedDeal' && <Countdown additionalParameters={this.additionalParameters}/>}
            </div>
          </div>

          <div className={`card-body ${backgroundImage ? 'hasImage' : ''}`}>
            {variant !== 'timedDeal' && (
              <Badge
                variant={badgeType}
                badgeText={badgeText}
                image={badgeImage}
                badgePromoType={badgePromoType}
                badgePriceInput={badgePriceInput}
                store={this.props.store || {}}
              />
            )}
            {!!text1 && (
              <p className="subtitle">{text1}</p>
            )}
            {!!text2 && (
              <span className="title">{text2}</span>
            )}
            {!!text3 && (
              <p className="editorial-text">{text3}</p>
            )}
            {variant === 'timedDeal' && (this.renderPrices())}
            <Icon
              className="arrow-button"
              name="ico-arrow-circle-right"
              size={150}
            />
          </div>
        </Link>
      </div>
    );
  }
}

BannerSingleProduct.propTypes = {
  products: PropTypes.array,
  productSelection: PropTypes.array,
  text1: PropTypes.string,
  text2: PropTypes.string,
  text3: PropTypes.string,
  backgroundImage: PropTypes.string,
  behindCoverImage: PropTypes.string,
  inFrontOfCoverImage: PropTypes.string,
  link: PropTypes.string,
  colourCode: PropTypes.string,
  'component-type': PropTypes.string,
  variant: PropTypes.string,
  googleTagMethods: PropTypes.func,
  badgeType: PropTypes.string,
  badgeImage: PropTypes.string,
  badgeText: PropTypes.string,
  badgePromoType: PropTypes.string,
  badgePriceInput: PropTypes.string,
  store: PropTypes.object,
  additionalParameters: PropTypes.string,
};
export default BannerSingleProduct;
